import { useEffect, useState } from "react";
import { Check, BarChart3, MessageSquare, TrendingUp, Mail, Phone, Search, SlidersHorizontal, Radio, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import useRequest from "../../components/hook/use-req";
import { Campaign, CountResponse } from "./types";
import Pagination from "../../components/pagination";
import { TopNav } from "../../components/layouts/navbar";

type ChannelType = 'ALL' | 'SMS' | 'EMAIL' | 'VOICE';

// Channel icon components matching the screenshot style
const ChannelIcon = ({ channel }: { channel: string }) => {
  const ch = channel?.toUpperCase();
  if (ch === 'SMS') return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-500 text-white">
      <MessageSquare size={15} />
    </span>
  );
  if (ch === 'EMAIL') return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 text-white">
      <Mail size={15} />
    </span>
  );
  if (ch === 'VOICE') return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-500 text-white">
      <Phone size={15} />
    </span>
  );
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-400 text-white">
      <BarChart3 size={15} />
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; dot: string; bg: string; text: string }> = {
    ACTIVE: { label: 'Live', dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700' },
    COMPLETED: { label: 'Completed', dot: 'bg-blue-400', bg: 'bg-blue-50', text: 'text-blue-700' },
    DRAFT: { label: 'Draft', dot: 'bg-gray-400', bg: 'bg-gray-100', text: 'text-gray-600' },
    NOT_STARTED: { label: 'Waiting', dot: 'bg-yellow-400', bg: 'bg-yellow-50', text: 'text-yellow-700' },
    PAUSED: { label: 'Paused', dot: 'bg-orange-400', bg: 'bg-orange-50', text: 'text-orange-700' },
  };
  const s = map[status] ?? { label: status, dot: 'bg-gray-400', bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const ChannelPerf = ({ deliveryRate, clickRate, sent }: { deliveryRate: number; clickRate: number; sent: number }) => {
  if (sent === 0) return (
    <div className="text-xs text-gray-400">
      <div className="font-medium text-gray-300 mb-0.5">--</div>
      No Data
    </div>
  );

  let label = 'Optimal';
  let barColor = 'bg-purple-500';
  if (clickRate >= 20) { label = 'High Performing'; barColor = 'bg-green-500'; }
  else if (clickRate >= 10) { label = 'Optimal'; barColor = 'bg-purple-500'; }
  else { label = 'Low Reach'; barColor = 'bg-blue-400'; }

  return (
    <div className="min-w-[90px]">
      <div className="text-sm font-semibold text-gray-800 mb-0.5">{clickRate.toFixed(1)}%</div>
      <div className={`h-1 w-12 rounded-full ${barColor} mb-1`} />
      <div className="text-xs text-gray-400 flex items-center gap-1">
        {label}
        <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-gray-300 text-[9px] text-gray-400">?</span>
      </div>
    </div>
  );
};

const CampaignAvatar = ({ name, status }: { name: string; status: string }) => {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-green-100',
    COMPLETED: 'bg-blue-100',
    DRAFT: 'bg-yellow-100',
    NOT_STARTED: 'bg-purple-100',
  };
  return (
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-400 ${colors[status] ?? 'bg-gray-100'}`}>
      {name?.charAt(0)?.toUpperCase() ?? '?'}
    </div>
  );
};

const MyCampaign: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [channelFilter, setChannelFilter] = useState("All");
  const [sortBy, setSortBy] = useState("created_at");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeChannel, setActiveChannel] = useState<ChannelType>('ALL');
  const [showFilters, setShowFilters] = useState(false);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [count, setCount] = useState<CountResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const { makeRequest: getCampaigns } = useRequest(`/campaign`, "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getCampaignsCount } = useRequest(`/campaign/summary`, "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getCampaignsCount();
      setCount(response?.response);
    };
    fetchData();
  }, []);

  function updateUrlParams(params: { [key: string]: string | number | null }) {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((key) => {
      if (params[key]) url.searchParams.set(key, params[key]!.toString());
      else url.searchParams.delete(key);
    });
    window.history.pushState({}, "", url.toString());
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    updateUrlParams({
      page: currentPage !== 1 ? currentPage : null,
      channel: channelFilter !== "All" ? channelFilter.toLowerCase() : null,
      status: statusFilter !== "All" ? statusFilter : null,
      search: debouncedSearchTerm ? debouncedSearchTerm : null,
    });
    fetchData();
  }, [currentPage, channelFilter, statusFilter, debouncedSearchTerm, sortBy]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const p: Record<string, any> = { limit: itemsPerPage, page: currentPage };
      if (debouncedSearchTerm.trim()) p.search = debouncedSearchTerm.trim();
      if (statusFilter !== "All") p.status = statusFilter;
      if (channelFilter !== "All") p.channel = channelFilter.toLowerCase();
      if (sortBy) { p.sort_by = sortBy; p.sort_order = sortBy === "campaign_name" ? "asc" : "desc"; }
      const [response] = await getCampaigns(undefined, p);
      if (response && Array.isArray(response.campaigns)) {
        setCampaigns(response.campaigns);
        if (response.pagination?.total) setTotalPages(Math.ceil(response.pagination.total / itemsPerPage));
        else setTotalPages(1);
      } else {
        setError("Invalid response format");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleActiveChannelChange = (channel: ChannelType) => {
    setActiveChannel(channel);
    const map: Record<ChannelType, string> = { SMS: 'sms', EMAIL: 'email', VOICE: 'voice', ALL: 'All' };
    setChannelFilter(map[channel]);
    setCurrentPage(1);
  };

  function handleViewCampaign(campaign: Campaign) {
    if (campaign.campaign_status === "DRAFT") {
      localStorage.setItem("draftCampaignId", campaign.campaign_id);
      localStorage.setItem("draftAudienceId", campaign.audience_id);
      navigate('/campaigns/create');
    } else {
      navigate(`/campaign-report/${campaign.campaign_id}`);
    }
  }

  function editDraftedCampaign(campaign_id: string, audience_id: string) {
    localStorage.setItem("draftCampaignId", campaign_id);
    localStorage.setItem("draftAudienceId", audience_id);
    navigate('/campaigns/create');
  }

  async function handlePageChange(page: number) {
    setCurrentPage(page);
    await fetchData();
  }

  const getTotalMetrics = () => {
    if (!count?.data) return null;
    const { SMS, EMAIL, VOICE } = count.data;
    return {
      total_campaigns: (SMS?.total_campaigns || 0) + (EMAIL?.total_campaigns || 0) + (VOICE?.total_campaigns || 0),
      active_campaigns: (SMS?.active_campaigns || 0) + (EMAIL?.active_campaigns || 0) + (VOICE?.active_campaigns || 0),
      messages_sent: (SMS?.messages_sent || 0) + (EMAIL?.messages_sent || 0) + (VOICE?.messages_sent || 0),
      total_clicks: (SMS?.total_clicks || 0) + (EMAIL?.total_clicks || 0) + (VOICE?.total_clicks || 0),
      unique_clicks: (SMS?.unique_clicks || 0) + (EMAIL?.unique_clicks || 0) + (VOICE?.unique_clicks || 0),
    };
  };

  const getCurrentChannelMetrics = () => {
    if (!count?.data) return null;
    if (activeChannel === 'ALL') return getTotalMetrics();
    return count.data[activeChannel];
  };

  const currentMetrics = getCurrentChannelMetrics();

  const SortableHeader = ({ label, value }: { label: string; value: string }) => (
    <th
      className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
      onClick={() => { setSortBy(value); setCurrentPage(1); }}
    >
      <span className="flex items-center gap-1">
        {label}
        <svg className={`w-3 h-3 transition-opacity ${sortBy === value ? 'opacity-100 text-blue-500' : 'opacity-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      
      <div className="min-h-screen">
        <div className="space-y-5">

          {/* Channel Tabs */}
          {/* <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 w-fit">
          {([
            { key: 'ALL', label: 'All Channels' },
            { key: 'SMS', label: 'SMS' },
            { key: 'EMAIL', label: 'Email' },
            { key: 'VOICE', label: 'Voice' },
          ] as { key: ChannelType; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handleActiveChannelChange(key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeChannel === key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div> */}

          {/* Main card */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

            {/* Toolbar */}
            <div className="px-6 pt-6 pb-4 flex items-center justify-between gap-4 border-b border-gray-100">
              {/* Create Campaign */}

              <button onClick={() => navigate('/campaigns/create')}  className="bg-[#4338CA] flex gap-2 p-2 text-[14px] rounded-md text-white font-medium">New Broadcast
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.9991 14.3147C12.6009 14.3155 13.1785 14.0772 13.6047 13.6523C14.031 13.2274 14.271 12.6506 14.2721 12.0487C14.2709 11.4469 14.0308 10.8702 13.6045 10.4454C13.1783 10.0206 12.6008 9.78241 11.9991 9.78321C11.3975 9.78254 10.8202 10.0207 10.3941 10.4454C9.96798 10.8701 9.72789 11.4466 9.72656 12.0482C9.72749 12.6501 9.9674 13.2269 10.3936 13.652C10.8197 14.077 11.3972 14.3154 11.9991 14.3147Z" fill="white" stroke="white" stroke-width="2" stroke-linejoin="round" />
                  <path d="M7.99975 7.5C5.33325 9.985 5.33325 14.015 7.99975 16.5M15.9998 16.5C18.6663 14.015 18.6663 9.985 15.9998 7.5M4.92875 5C1.02375 8.893 1.02375 15.205 4.92875 19.098M19.0707 19.098C22.9757 15.205 22.9757 8.893 19.0707 5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

              </button>
              {/* Filters toggle */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search Broadcasts"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  />
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                >
                  <SlidersHorizontal size={15} />
                  Filters
                </button>

              </div>
            </div>

            {/* Expandable filters */}
            {showFilters && (
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex gap-3 flex-wrap">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="All">All Statuses</option>
                  <option value="ACTIVE">Live</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="DRAFT">Draft</option>
                  <option value="NOT_STARTED">Waiting</option>
                </select>
                <select
                  value={channelFilter}
                  onChange={(e) => { setChannelFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="All">All Channels</option>
                  <option value="sms">SMS</option>
                  <option value="voice">Voice</option>
                  <option value="email">Email</option>
                    <option value="whatsapp">Whatsapp</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="created_at">Sort: Date Created</option>
                  <option value="campaign_name">Sort: Name</option>
                  <option value="campaign_status">Sort: Status</option>
                  <option value="sent">Sort: Messages Sent</option>
                  <option value="attempted">Sort: Attempted</option>
                </select>
              </div>
            )}

            {/* Table */}
            {campaigns.length === 0 ? (
              <div className="py-20 text-center">
                <Radio size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No campaigns found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Broadcasts</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Channel</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                        <SortableHeader label="Leads" value="campaign_size" />
                        <SortableHeader label="Delivered ↑" value="sent" />
                        <SortableHeader label="Opened ↑" value="attempted" />
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Channel Perf. ↑</th>
                        <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {campaigns.map((campaign) => {
                        const deliveryRate = campaign?.analytics?.attempted > 0
                          ? (campaign.analytics.sent / campaign.analytics.attempted) * 100 : 0;
                        const clickRate = campaign?.analytics?.sent > 0
                          ? (campaign.analytics.unique_clicks / campaign.analytics.sent) * 100 : 0;

                        const dateLabel = campaign.scheduled_start
                          ? new Date(campaign.scheduled_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : new Date(campaign.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const recipientCount = campaign.campaign_size
                          ? campaign.campaign_size >= 1000
                            ? `${(campaign.campaign_size / 1000).toFixed(0)}k recipients`
                            : `${campaign.campaign_size} recipients`
                          : '0 recp.';
                        const isDraft = campaign.campaign_status === 'DRAFT';

                        return (
                          <tr
                            key={campaign.campaign_id}
                            className="hover:bg-gray-50/70 transition-colors cursor-pointer group"
                            onClick={() => handleViewCampaign(campaign)}
                          >
                            {/* Campaign name */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <CampaignAvatar name={campaign.campaign_name} status={campaign.campaign_status} />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {campaign.campaign_name}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-0.5">
                                    {isDraft ? 'Draft' : dateLabel} · {recipientCount}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Channel icon */}
                            <td className="px-5 py-4">
                              <ChannelIcon channel={campaign.campaign_channel} />
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4">
                              <StatusBadge status={campaign.campaign_status} />
                            </td>

                            {/* Leads / Campaign size */}
                            <td className="px-5 py-4 text-sm text-gray-700 font-medium">
                              {campaign.campaign_size?.toLocaleString() ?? '—'}
                            </td>

                            {/* Delivered (sent) */}
                            <td className="px-5 py-4 text-sm text-gray-700 font-medium">
                              {campaign.analytics.sent?.toLocaleString() ?? '—'}
                            </td>

                            {/* Opened (attempted) */}
                            <td className="px-5 py-4 text-sm text-gray-700 font-medium">
                              {campaign.analytics.attempted?.toLocaleString() ?? '—'}
                            </td>

                            {/* Channel perf */}
                            <td className="px-5 py-4">
                              <ChannelPerf
                                deliveryRate={deliveryRate}
                                clickRate={clickRate}
                                sent={campaign.analytics.sent}
                              />
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                {campaign.campaign_status !== 'DRAFT' && campaign.campaign_status !== 'NOT_STARTED' && (
                                  <button
                                    onClick={() => handleViewCampaign(campaign)}
                                    className="px-3 py-1 text-xs text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                                  >
                                    View
                                  </button>
                                )}
                                {campaign.campaign_status === 'DRAFT' && (
                                  <button
                                    onClick={() => editDraftedCampaign(campaign.campaign_id, campaign.audiences[0])}
                                    className="px-3 py-1 text-xs text-amber-600 border border-amber-200 rounded-md hover:bg-amber-50 transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                                {campaign.campaign_status === 'NOT_STARTED' && (
                                  <button
                                    onClick={() => handleViewCampaign(campaign)}
                                    className="px-3 py-1 text-xs text-amber-600 border border-amber-200 rounded-md hover:bg-amber-50 transition-colors"
                                  >
                                    View
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-100">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyCampaign;